/**
 * Provider-side document upload panel for `document_upload` items assigned
 * to the provider in a hiring process.
 *
 * Two paths:
 * 1. Attach an existing credential from the library (attach-existing)
 * 2. Upload a new file via TUS — optionally save to library (upload-new)
 *
 * Attached files are listed with a remove button.
 */
import React, { useEffect, useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Library,
  Upload,
  X,
  FileText,
  Loader2,
  Check,
  BookmarkPlus,
  CheckCircle2,
  Eye,
  Sparkles,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { getCredentialFileUrls } from "@/ajax/documents/credential-file-urls";
import { ChatAttachmentPreviewModal } from "@/features/chat/ChatAttachmentPreviewModal";
import {
  CredentialFileRendition,
  HiringProcessItemStatus,
} from "@api/shared-runtime/enums";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTRPC, useTRPCClient } from "@/trpc";
import { queryClient } from "@/ajax/queryClient";
import type { DeclaredUploadFile } from "@/ajax/upload/declare-upload-file";
import {
  FileUploadInput,
  UploadedFile,
} from "@/components/file-upload/file-upload-input";
import type { RouterOutputs } from "@/trpc";

type AttachableCredential =
  RouterOutputs["hiringProcess"]["listProviderAttachableCredentials"]["candidates"][number];

/**
 * Resolve a usable mime type for the file preview modal, which strictly
 * branches on `application/pdf` and `image/*`. Credentials may have
 * unreliable server-reported mime types (declared at upload, not sniffed)
 * or none at all (legacy versions). The filename extension is the source
 * of truth — same approach as `FileVersionPreview.tsx`.
 */
function inferMimeTypeFromFileName(
  fileName: string,
  serverMimeType: string | null,
): string {
  const ext = fileName.toLowerCase().split(".").pop() ?? "";
  switch (ext) {
    case "pdf":
      return "application/pdf";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "gif":
      return "image/gif";
    case "webp":
      return "image/webp";
    case "heic":
    case "heif":
      return "image/heic";
    default:
      return serverMimeType ?? "application/octet-stream";
  }
}

// ─── Attach-existing subpanel ─────────────────────────────────────────────────

type CredentialPreviewState = {
  url: string;
  fileName: string;
  mimeType: string;
};

function CredentialRow({
  cred,
  selected,
  onSelect,
  onPreview,
  isPreviewLoading,
}: {
  cred: AttachableCredential;
  selected: boolean;
  onSelect: () => void;
  onPreview: () => void;
  isPreviewLoading: boolean;
}) {
  const attached = cred.alreadyAttached;
  return (
    <button
      type="button"
      onClick={attached ? undefined : onSelect}
      disabled={attached}
      aria-disabled={attached}
      className={`w-full flex items-center gap-3 rounded-md border px-3 py-2.5 text-left text-sm transition-colors ${
        attached
          ? "border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed"
          : selected
            ? "border-blue-300 bg-blue-50 text-primary"
            : "border-gray-200 bg-white text-gray-800 hover:border-gray-300 hover:bg-gray-50"
      }`}
    >
      <FileText
        size={16}
        className={`shrink-0 ${attached ? "text-gray-400" : "text-blue-500"}`}
      />
      <span className="flex-1 min-w-0">
        <span className="block truncate font-medium">{cred.displayName}</span>
        {cred.fileName && cred.fileName !== cred.displayName && (
          <span className="block truncate text-xs text-gray-400">
            {cred.fileName}
          </span>
        )}
      </span>
      {attached && (
        <Badge
          variant="outline"
          className="text-[10px] h-4 px-1.5 bg-green-50 text-green-700 border-green-200 shrink-0"
        >
          <Check size={10} className="mr-0.5" />
          Attached
        </Badge>
      )}
      {/* Preview action — stops button-click propagation so it doesn't toggle
          selection. Spinner replaces the eye while the signed URL is in
          flight. Available even on already-attached rows so the provider
          can still see what they submitted. */}
      <span
        role="button"
        tabIndex={0}
        aria-label="Preview credential"
        onClick={(e) => {
          e.stopPropagation();
          if (!isPreviewLoading) {
            onPreview();
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            if (!isPreviewLoading) {
              onPreview();
            }
          }
        }}
        className="shrink-0 text-gray-400 hover:text-blue-600 p-1 rounded transition-colors cursor-pointer"
      >
        {isPreviewLoading ? (
          <Loader2 size={14} className="animate-spin" />
        ) : (
          <Eye size={14} />
        )}
      </span>
      {selected && !attached && (
        <Check size={14} className="shrink-0 text-blue-600" />
      )}
    </button>
  );
}

function AttachFromLibraryPanel({
  hiringProcessItemProgressId,
  onAttached,
}: {
  hiringProcessItemProgressId: string;
  onAttached: () => void;
}) {
  const trpc = useTRPC();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedDisplayName, setSelectedDisplayName] = useState<string>("");
  const [previewFile, setPreviewFile] = useState<CredentialPreviewState | null>(
    null,
  );
  const [previewingId, setPreviewingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery(
    trpc.hiringProcess.listProviderAttachableCredentials.queryOptions({
      hiringProcessItemProgressId,
    }),
  );

  const attachMutation = useMutation(
    trpc.hiringProcess.attachExistingCredential.mutationOptions({
      onSuccess: async () => {
        toast.success("Credential attached.");
        // Clear the active selection and refresh the picker so the just-
        // attached row flips to its disabled "Attached" state. Without
        // this the same credential stays available for a duplicate
        // attach until the modal is closed and reopened.
        setSelectedId(null);
        setSelectedDisplayName("");
        await queryClient.invalidateQueries(
          trpc.hiringProcess.listProviderAttachableCredentials.queryOptions({
            hiringProcessItemProgressId,
          }),
        );
        onAttached();
      },
      onError: (err) => {
        toast.error(`Failed to attach: ${err.message}`);
      },
    }),
  );

  const candidates = data?.candidates ?? [];
  const hasHint = data?.hint.credentialFileTypeId != null;
  const recommended = candidates.filter((c) => c.isRecommended);
  const others = candidates.filter((c) => !c.isRecommended);

  const handleAttach = async () => {
    if (!selectedId) {
      return;
    }
    // The org already knows the context (item title, credential type hint);
    // use the selected credential's user-facing display name as the label.
    const fallback = selectedDisplayName.trim() || "Credential";
    await attachMutation.mutateAsync({
      hiringProcessItemProgressId,
      userCredentialsFileVersionId: selectedId,
      label: fallback.slice(0, 200),
    });
  };

  const handlePreview = async (cred: AttachableCredential) => {
    if (previewingId) {
      return;
    }
    setPreviewingId(cred.activeFileVersionId);
    try {
      // The API signs by version id after checking it is the caller's own,
      // scanned file; `cred.preview` is only for clients that sign it.
      const [signed] = await getCredentialFileUrls({
        versions: [
          {
            versionId: cred.activeFileVersionId,
            rendition: CredentialFileRendition.Original,
          },
        ],
      });
      if (!signed.url) {
        throw new Error("No signed URL returned");
      }
      setPreviewFile({
        url: signed.url,
        fileName: cred.fileName || cred.displayName,
        // Extension-based mime detection mirrors `FileVersionPreview` — the
        // server-reported `fileUpload.mimeType` is user-declared at upload
        // and unreliable for credentials (see CLAUDE_LEARNINGS:
        // "Supabase bucket allowed_mime_types validates the declared
        // Content-Type, not bytes"). It's also null for legacy versions
        // that predate the `file_upload` row entirely.
        mimeType: inferMimeTypeFromFileName(cred.fileName, signed.mimeType),
      });
    } catch (err) {
      toast.error(
        `Couldn't open preview: ${
          err instanceof Error ? err.message : "unknown error"
        }`,
      );
    } finally {
      setPreviewingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading credentials...
      </div>
    );
  }

  if (candidates.length === 0) {
    return (
      <p className="text-sm text-gray-500 py-2">
        Your credentials library is empty. Switch to the Upload tab to add a new
        file.
      </p>
    );
  }

  const renderRow = (cred: AttachableCredential) => {
    const isSelected = selectedId === cred.activeFileVersionId;
    return (
      <CredentialRow
        key={cred.activeFileVersionId}
        cred={cred}
        selected={isSelected}
        onSelect={() => {
          // Toggle: clicking the already-selected row clears the selection
          // (the Attach button then hides, returning the picker to its
          // initial state).
          if (isSelected) {
            setSelectedId(null);
            setSelectedDisplayName("");
          } else {
            setSelectedId(cred.activeFileVersionId);
            setSelectedDisplayName(cred.displayName);
          }
        }}
        onPreview={() => handlePreview(cred)}
        isPreviewLoading={previewingId === cred.activeFileVersionId}
      />
    );
  };

  return (
    <div className="space-y-3">
      {/* Recommended group — only shown when the item has a credential-type
          hint and the provider has at least one matching credential in
          their library. Mirrors the type-match nudge from the upload-new
          tab (which also defaults to save-to-library when hinted). */}
      {hasHint && recommended.length > 0 && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5">
            <Sparkles size={12} className="text-amber-500" />
            <p className="text-xs font-medium text-gray-700">Recommended</p>
            <Badge
              variant="outline"
              className="text-[10px] h-4 px-1.5 bg-amber-50 text-amber-700 border-amber-200"
            >
              matches credential type
            </Badge>
          </div>
          <div className="space-y-1.5">{recommended.map(renderRow)}</div>
        </div>
      )}

      {others.length > 0 && (
        <div className="space-y-1.5">
          {hasHint && recommended.length > 0 && (
            <p className="text-xs font-medium text-gray-500">
              Other credentials
            </p>
          )}
          <div className="space-y-1.5">{others.map(renderRow)}</div>
        </div>
      )}

      {selectedId && (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            className="h-8 text-xs"
            onClick={handleAttach}
            disabled={attachMutation.isPending}
          >
            {attachMutation.isPending ? (
              <Loader2 size={12} className="mr-1.5 animate-spin" />
            ) : (
              <Check size={12} className="mr-1.5" />
            )}
            Attach Credential
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 text-xs text-gray-500"
            onClick={() => {
              setSelectedId(null);
              setSelectedDisplayName("");
            }}
            disabled={attachMutation.isPending}
          >
            Cancel
          </Button>
        </div>
      )}

      {previewFile && (
        <ChatAttachmentPreviewModal
          url={previewFile.url}
          fileName={previewFile.fileName}
          mimeType={previewFile.mimeType}
          onClose={() => setPreviewFile(null)}
        />
      )}
    </div>
  );
}

// ─── Upload-new subpanel ──────────────────────────────────────────────────────

function UploadNewPanel({
  hiringProcessItemProgressId,
  hasHint,
  onUploaded,
}: {
  hiringProcessItemProgressId: string;
  /** Whether the org provided a credential-type hint (flips default save-to-library) */
  hasHint: boolean;
  onUploaded: () => void;
}) {
  const trpcClient = useTRPCClient();
  const [saveToLibrary, setSaveToLibrary] = useState(hasHint);
  const [files, setFiles] = useState<UploadedFile[]>([]);

  // `FileUploadInput` only carries `fileUploadId`, but the post-upload
  // confirm step needs the attachment id. Hold a per-file fileUploadId →
  // attachmentId mapping populated by `createUpload` and read by
  // `handleFileUploaded` when the TUS transfer completes.
  const attachmentIdByFileUploadId = useRef(new Map<string, string>());

  const createUpload = async (file: DeclaredUploadFile) => {
    // Label always defaults to the file name — the org already knows the
    // context (item title + credential type hint), so we don't ask the
    // provider for redundant copy.
    const result =
      await trpcClient.hiringProcess.uploadProviderAttachment.mutate({
        hiringProcessItemProgressId,
        fileName: file.fileName,
        fileSizeBytes: file.fileSizeBytes,
        mimeType: file.mimeType,
        sha256: file.sha256,
        saveToLibrary,
        label: file.fileName.slice(0, 200),
      });
    attachmentIdByFileUploadId.current.set(
      result.fileUploadId,
      result.attachmentId,
    );
    return {
      id: result.fileUploadId,
      storagePath: result.storagePath,
      bucketName: result.bucketName,
      uploadToken: result.uploadToken,
    };
  };

  const handleFileUploaded = async (file: UploadedFile) => {
    setFiles((prev) => [...prev, file]);
    const attachmentId = attachmentIdByFileUploadId.current.get(
      file.fileUploadId,
    );
    if (attachmentId) {
      attachmentIdByFileUploadId.current.delete(file.fileUploadId);
      // Await confirmation before triggering parent refresh. The confirm
      // endpoint flips `scan_status` off `awaiting_upload` and enqueues the
      // scan + extraction Inngest events; the completion check on the
      // server rejects items whose attachments are still `awaiting_upload`,
      // so refreshing too early would render an attachment the user can
      // see but cannot actually submit.
      try {
        await trpcClient.hiringProcess.confirmProviderAttachmentUploaded.mutate(
          { attachmentId },
        );
      } catch (err) {
        console.error("Failed to confirm attachment upload", err);
        toast.error(
          err instanceof Error
            ? `Couldn't finalize upload: ${err.message}`
            : "Couldn't finalize upload.",
        );
        // Fall through to onUploaded — the attachment row exists and the
        // file is in storage, but the parent should still refresh so the
        // provider sees the entry (with pending scan state) and can retry
        // by removing + re-uploading.
      }
    }
    onUploaded();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Switch
          id={`save-to-library-${hiringProcessItemProgressId}`}
          checked={saveToLibrary}
          onCheckedChange={setSaveToLibrary}
        />
        <Label
          htmlFor={`save-to-library-${hiringProcessItemProgressId}`}
          className="text-xs text-gray-700 cursor-pointer flex items-center gap-1.5"
        >
          <BookmarkPlus size={12} className="text-blue-500" />
          Save to my credentials library for future use
        </Label>
      </div>
      {!hasHint && !saveToLibrary && (
        <p className="text-xs text-gray-400">
          This file will only be shared with this employer.
        </p>
      )}
      {hasHint && saveToLibrary && (
        <p className="text-xs text-blue-600">
          This document will be saved to your credentials library — you can
          reuse it on future applications.
        </p>
      )}

      <FileUploadInput
        files={files}
        onFileUploaded={handleFileUploaded}
        onFileRemoved={(id) =>
          setFiles((prev) => prev.filter((f) => f.fileUploadId !== id))
        }
        createUpload={createUpload}
        maxFiles={5}
      />
    </div>
  );
}

// ─── Attached-file list ────────────────────────────────────────────────────────

function AttachedFileRow({
  attachment,
  onRemove,
  isRemoving,
}: {
  attachment: { id: string; label: string };
  onRemove: (id: string) => void;
  isRemoving: boolean;
}) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
      <FileText size={14} className="shrink-0 text-gray-400" />
      <span className="flex-1 min-w-0 truncate text-gray-700">
        {attachment.label}
      </span>
      <button
        type="button"
        aria-label="Remove attachment"
        onClick={() => onRemove(attachment.id)}
        disabled={isRemoving}
        className="shrink-0 text-gray-400 hover:text-red-500 transition-colors p-0.5 rounded disabled:opacity-50"
      >
        {isRemoving ? (
          <Loader2 size={14} className="animate-spin" />
        ) : (
          <X size={14} />
        )}
      </button>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface ProviderDocumentUploadProps {
  /**
   * Existing `hiringProcessItemProgress.id` for this candidate × item, or
   * null if the provider has not yet acted on this item. When null the
   * component lazily upserts the row via `ensureProviderProgressRow` so
   * library-list/upload flows have a stable anchor id.
   */
  hiringProcessItemProgressId: string | null;
  /** Template item id — used to ensure the progress row when one is missing. */
  hiringProcessItemId: string;
  /** Parent decision's job post id — used to ensure the progress row. */
  jobPostId: string;
  /**
   * The credential-type hint id the org set on the template item, or null.
   * Drives both the default tab + save-to-library behaviour AND a visible
   * banner above the picker so the provider knows what type of credential
   * is being requested. The name is resolved client-side via the
   * `credentialFiles.getFileTypes` query (cached across the app).
   */
  requestedCredentialFileTypeId: number | null;
  /** Existing provider attachments to display (from getMyProcess item data) */
  existingAttachments?: Array<{ id: string; label: string }>;
  /** Called when any attachment changes (add or remove) to trigger parent refresh */
  onAttachmentsChanged?: () => void;
  /**
   * Current status of the provider's progress row. Drives the submit/reopen
   * footer — when `Completed`, the picker is hidden and a "Reopen" link is
   * shown alongside a "Submitted" badge. Mid-process this is `NotStarted`
   * or `InProgress`.
   */
  status: HiringProcessItemStatus;
  /**
   * Mark the item complete. Called when the provider clicks Submit after
   * uploading/attaching at least one file. Parent owns the
   * `updateItemStatus` mutation so it can invalidate the right caches.
   */
  onMarkComplete: () => void;
  /** Reopen a completed item. Called from the "Reopen" link. */
  onReopen: () => void;
  /** True while the parent's status mutation is in flight, to disable the buttons. */
  isUpdatingStatus?: boolean;
}

export function ProviderDocumentUpload({
  hiringProcessItemProgressId,
  hiringProcessItemId,
  jobPostId,
  requestedCredentialFileTypeId,
  existingAttachments = [],
  onAttachmentsChanged,
  status,
  onMarkComplete,
  onReopen,
  isUpdatingStatus = false,
}: ProviderDocumentUploadProps) {
  const trpc = useTRPC();
  const hasCredentialHint = requestedCredentialFileTypeId != null;
  // Resolve the type name for the hint banner. `getFileTypes` returns the
  // full list of credential types (small reference table, ~50 rows) and is
  // shared across the template editor + library picker, so this hits a
  // warm cache in practice.
  const { data: credentialFileTypes } = useQuery(
    trpc.credentialFiles.getFileTypes.queryOptions(undefined, {
      enabled: hasCredentialHint,
    }),
  );
  const requestedCredentialFileTypeName =
    hasCredentialHint && credentialFileTypes
      ? (credentialFileTypes.find((t) => t.id === requestedCredentialFileTypeId)
          ?.name ?? null)
      : null;
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [ensuredProgressId, setEnsuredProgressId] = useState<string | null>(
    null,
  );

  const effectiveProgressId = hiringProcessItemProgressId ?? ensuredProgressId;

  const ensureProgressMutation = useMutation(
    trpc.hiringProcess.ensureProviderProgressRow.mutationOptions({
      onSuccess: (data) => setEnsuredProgressId(data.id),
      onError: (err) => {
        toast.error(`Failed to initialize upload: ${err.message}`);
      },
    }),
  );

  // Ensure the progress row exists before rendering the picker panels. Both
  // panels need a stable progress id (the library list query, the upload
  // mutation, the attach mutation). The ensure call is idempotent — the
  // server upserts against the (userId, jobPostId, itemId) unique index.
  useEffect(() => {
    if (
      hiringProcessItemProgressId == null &&
      ensuredProgressId == null &&
      !ensureProgressMutation.isPending
    ) {
      ensureProgressMutation.mutate({ hiringProcessItemId, jobPostId });
    }
  }, [
    hiringProcessItemProgressId,
    ensuredProgressId,
    hiringProcessItemId,
    jobPostId,
    ensureProgressMutation,
  ]);

  const removeAttachmentMutation = useMutation(
    trpc.hiringProcess.removeProviderAttachment.mutationOptions({
      onSuccess: () => {
        toast.success("Attachment removed.");
        setRemovingId(null);
        onAttachmentsChanged?.();
      },
      onError: (err) => {
        toast.error(`Failed to remove: ${err.message}`);
        setRemovingId(null);
      },
    }),
  );

  const handleRemove = (attachmentId: string) => {
    setRemovingId(attachmentId);
    removeAttachmentMutation.mutate({ attachmentId });
  };

  const handleAttachmentAdded = () => {
    onAttachmentsChanged?.();
  };

  const isCompleted = status === HiringProcessItemStatus.Completed;
  const hasAttachments = existingAttachments.length > 0;

  return (
    <div className="mt-2 space-y-3">
      {/* Existing attachments — always render the read-only list, even when
          completed, so the provider can see what they submitted. The remove
          button on each row is disabled server-side once status=Completed. */}
      {hasAttachments && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-gray-500">
            {isCompleted ? "Submitted files" : "Attached files"}
          </p>
          {existingAttachments.map((att) => (
            <AttachedFileRow
              key={att.id}
              attachment={att}
              onRemove={handleRemove}
              isRemoving={removingId === att.id}
            />
          ))}
        </div>
      )}

      {/* Hint banner — tells the provider what type of credential the org
          is asking for. Without this the only signal that a hint exists is
          the default-tab + recommended-group behavior, which a busy provider
          can easily miss. Shown above the picker (and persists once the
          item is completed so the provider can verify what they sent). */}
      {hasCredentialHint && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 flex items-start gap-2">
          <Sparkles size={14} className="mt-0.5 shrink-0 text-amber-600" />
          <p className="text-xs text-amber-900">
            The organization is asking for:{" "}
            <span className="font-medium">
              {requestedCredentialFileTypeName ?? "a specific credential type"}
            </span>
            . Attach an existing one from your library, or upload a fresh copy
            (saved back to your library by default).
          </p>
        </div>
      )}

      {/* Picker tabs — hidden once submitted to make the read-only state
          obvious; provider must Reopen to add or replace files. */}
      {!isCompleted && (
        <div className="rounded-md border border-gray-200 bg-white p-3">
          {effectiveProgressId == null ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Preparing upload…
            </div>
          ) : (
            <Tabs defaultValue={hasCredentialHint ? "library" : "upload"}>
              <TabsList className="h-8 text-xs mb-3">
                <TabsTrigger value="library" className="text-xs h-7">
                  <Library size={12} className="mr-1.5" />
                  Use existing credential
                </TabsTrigger>
                <TabsTrigger value="upload" className="text-xs h-7">
                  <Upload size={12} className="mr-1.5" />
                  Upload new
                </TabsTrigger>
              </TabsList>

              <TabsContent value="library">
                <AttachFromLibraryPanel
                  hiringProcessItemProgressId={effectiveProgressId}
                  onAttached={handleAttachmentAdded}
                />
              </TabsContent>

              <TabsContent value="upload">
                <UploadNewPanel
                  hiringProcessItemProgressId={effectiveProgressId}
                  hasHint={hasCredentialHint}
                  onUploaded={handleAttachmentAdded}
                />
              </TabsContent>
            </Tabs>
          )}
        </div>
      )}

      {/* Submit / Reopen footer — provider explicitly transitions the item
          to Completed once they've attached at least one file. Without this
          the upload UI has no terminal action and the task sits open
          forever. Mirrors the org-side `DocumentUploadPanel` pattern.
          Sticky to the bottom of the scroll container (checklist dialog
          and inline view both wrap this in a max-h + overflow-y-auto)
          so the action stays in view on short windows. */}
      <div className="sticky bottom-0 -mx-1 px-1 py-2 bg-white border-t border-gray-100 flex items-center justify-end gap-2">
        {isCompleted ? (
          <>
            <span className="inline-flex items-center gap-1 text-xs text-green-700">
              <Check size={12} />
              Submitted
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 px-2.5 text-xs"
              disabled={isUpdatingStatus}
              onClick={onReopen}
            >
              Reopen
            </Button>
          </>
        ) : (
          <>
            <span className="text-xs text-gray-500">
              {hasAttachments
                ? "Finished uploading? Submit to complete this step."
                : "Attach at least one file, then submit."}
            </span>
            <Button
              type="button"
              size="sm"
              className="h-7 px-3 text-xs"
              disabled={!hasAttachments || isUpdatingStatus}
              onClick={onMarkComplete}
            >
              <CheckCircle2 size={12} className="mr-1" />
              Submit
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
